import { useState, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import axios from 'axios';
import { useAuth } from '@/contexts/AuthContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Card, CardContent, CardHeader, CardTitle, CardDescription, CardFooter } from '@/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Upload, X, Palette, Info, Globe } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const AdminSettings = ({ aboutUs, onRefresh }) => {
  const { token } = useAuth();
  const { t } = useTranslation();
  
  // 关于我们设置 - 简化为单一输入
  const [formData, setFormData] = useState({ 
    title: '', 
    description: '', 
    image_url: '',
  });
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const fileInputRef = useRef(null);
  
  // ArtCanvas设置
  const [artCanvasData, setArtCanvasData] = useState({
    hero_background_image: '',
    tote_bag_image: '',
    hero_title: 'ArtCanvas',
    hero_subtitle: '发现独特的艺术之美',
    tote_bag_title: '把你的作品变成艺术品',
    tote_bag_description: '我们将你的画作印制在100%有机棉包上，让你的艺术走进日常生活。',
    royalty_percentage: 10,
    tote_bag_features: [
      '100%有机棉，环保可持续',
      '高清数码印刷，色彩持久',
      '多种尺寸可选，全球配送',
      '透明结算，按月支付版税'
    ],
    tote_bag_detail_link: '/about'
  });
  const [savingArtCanvas, setSavingArtCanvas] = useState(false);
  const [uploadingToteBag, setUploadingToteBag] = useState(false);
  const [uploadingHeroBg, setUploadingHeroBg] = useState(false);
  const [newFeature, setNewFeature] = useState('');
  const toteBagInputRef = useRef(null);
  const heroBgInputRef = useRef(null);

  useEffect(() => {
    if (aboutUs) {
      setFormData({
        title: aboutUs.title || '', 
        description: aboutUs.description || '',
        image_url: aboutUs.image_url || '',
      });
    }
    // 加载ArtCanvas设置
    fetchArtCanvasSettings();
  }, [aboutUs]);

  const fetchArtCanvasSettings = async () => {
    try {
      const res = await axios.get(`${API}/settings/artcanvas`);
      if (res.data) {
        setArtCanvasData({
          hero_background_image: res.data.hero_background_image || '',
          tote_bag_image: res.data.tote_bag_image || '',
          hero_title: res.data.hero_title || 'ArtCanvas',
          hero_subtitle: res.data.hero_subtitle || '发现独特的艺术之美',
          tote_bag_title: res.data.tote_bag_title || '把你的作品变成艺术品',
          tote_bag_description: res.data.tote_bag_description || '',
          royalty_percentage: res.data.royalty_percentage || 10,
          tote_bag_features: res.data.tote_bag_features || [
            '100%有机棉，环保可持续',
            '高清数码印刷，色彩持久',
            '多种尺寸可选，全球配送',
            '透明结算，按月支付版税'
          ],
          tote_bag_detail_link: res.data.tote_bag_detail_link || '/about'
        });
      }
    } catch (err) {
      console.error('Failed to fetch ArtCanvas settings:', err);
    }
  };

  // 获取完整的图片URL
  const getFullImageUrl = (url) => {
    if (!url) return '';
    if (url.startsWith('http')) return url;
    return `${BACKEND_URL}${url}`;
  };

  // 处理关于我们图片上传
  const handleImageUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    setUploading(true);
    try {
      const uploadFormData = new FormData();
      uploadFormData.append('file', file);

      const res = await axios.post(`${API}/upload/image`, uploadFormData, {
        headers: {
          'Content-Type': 'multipart/form-data',
          'Authorization': `Bearer ${token}`
        }
      });

      setFormData({ ...formData, image_url: res.data.url });
      toast.success('图片上传成功！');
    } catch (err) {
      console.error('Upload error:', err);
      toast.error('图片上传失败：' + (err.response?.data?.detail || '未知错误'));
    } finally {
      setUploading(false);
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  // 处理Hero背景图上传
  const handleHeroBgUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    setUploadingHeroBg(true);
    try {
      const uploadFormData = new FormData();
      uploadFormData.append('file', file);

      const res = await axios.post(`${API}/upload/image`, uploadFormData, {
        headers: {
          'Content-Type': 'multipart/form-data',
          'Authorization': `Bearer ${token}`
        }
      });

      setArtCanvasData({ ...artCanvasData, hero_background_image: res.data.url });
      toast.success('背景图上传成功！');
    } catch (err) {
      console.error('Upload error:', err);
      toast.error('图片上传失败：' + (err.response?.data?.detail || '未知错误'));
    } finally {
      setUploadingHeroBg(false);
      if (heroBgInputRef.current) {
        heroBgInputRef.current.value = '';
      }
    }
  };

  // 处理有机棉包图片上传
  const handleToteBagUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    setUploadingToteBag(true);
    try {
      const uploadFormData = new FormData();
      uploadFormData.append('file', file);

      const res = await axios.post(`${API}/upload/image`, uploadFormData, {
        headers: {
          'Content-Type': 'multipart/form-data',
          'Authorization': `Bearer ${token}`
        }
      });

      setArtCanvasData({ ...artCanvasData, tote_bag_image: res.data.url });
      toast.success('图片上传成功！');
    } catch (err) {
      console.error('Upload error:', err);
      toast.error('图片上传失败：' + (err.response?.data?.detail || '未知错误'));
    } finally {
      setUploadingToteBag(false);
      if (toteBagInputRef.current) {
        toteBagInputRef.current.value = '';
      }
    }
  };

  // 清除图片
  const handleClearImage = () => {
    setFormData({ ...formData, image_url: '' });
  };

  const handleClearHeroBgImage = () => {
    setArtCanvasData({ ...artCanvasData, hero_background_image: '' });
  };

  const handleClearToteBagImage = () => {
    setArtCanvasData({ ...artCanvasData, tote_bag_image: '' });
  };

  // 保存关于我们
  const handleSave = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/settings/about`, formData, { headers: { Authorization: `Bearer ${token}` } });
      toast.success('保存成功！');
      onRefresh();
    } catch (err) {
      toast.error('保存失败：' + (err.response?.data?.detail || '未知错误'));
    } finally {
      setSaving(false);
    }
  };

  // 获取当前语言的字段名
  const getTitleField = (lang) => lang === 'zh' ? 'title' : `title_${lang}`;
  const getDescField = (lang) => lang === 'zh' ? 'description' : `description_${lang}`;

  // 更新多语言字段
  const updateField = (field, value) => {
    setFormData({ ...formData, [field]: value });
  };

  // 保存ArtCanvas设置
  const handleSaveArtCanvas = async () => {
    setSavingArtCanvas(true);
    try {
      await axios.put(`${API}/settings/artcanvas`, artCanvasData, { 
        headers: { Authorization: `Bearer ${token}` } 
      });
      toast.success('ArtCanvas设置保存成功！');
    } catch (err) {
      toast.error('保存失败：' + (err.response?.data?.detail || '未知错误'));
    } finally {
      setSavingArtCanvas(false);
    }
  };

  return (
    <div data-testid="admin-settings">
      <h1 className="text-2xl font-bold mb-8">网站设置</h1>
      
      <Tabs defaultValue="about" className="w-full">
        <TabsList className="mb-6">
          <TabsTrigger value="about" className="flex items-center gap-2">
            <Info className="w-4 h-4" />
            关于我们
          </TabsTrigger>
          <TabsTrigger value="artcanvas" className="flex items-center gap-2">
            <Palette className="w-4 h-4" />
            ArtCanvas设置
          </TabsTrigger>
        </TabsList>

        {/* 关于我们设置 */}
        <TabsContent value="about">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Globe className="w-5 h-5" />
                关于我们 / About Us
              </CardTitle>
              <CardDescription>
                编辑首页"关于我们"板块的内容，其他语言将自动翻译显示
                <br />
                Edit the "About Us" section, other languages will be auto-translated
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              {/* 提示信息 */}
              <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 text-sm text-amber-700">
                💡 填写内容后，俄语、法语、日语等其他语言将自动翻译显示
              </div>

              {/* 标题输入 - 单一输入框 */}
              <div className="space-y-2">
                <Label>标题 / Title</Label>
                <Input 
                  value={formData.title} 
                  onChange={(e) => setFormData({ ...formData, title: e.target.value })} 
                  placeholder="例如：有机素食星球 / e.g., Organic Vegan Planet"
                  data-testid="about-title-input" 
                />
              </div>

              {/* 描述输入 - 单一输入框 */}
              <div className="space-y-2">
                <Label>描述 / Description</Label>
                <Textarea 
                  value={formData.description} 
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })} 
                  placeholder="介绍您的平台... / Introduce your platform..."
                  rows={4} 
                  data-testid="about-description-input" 
                />
              </div>

              {/* 图片上传 */}
              <div className="space-y-2">
                <Label>图片 / Image</Label>
                <div className="flex gap-2">
                  <Input 
                    value={formData.image_url} 
                    onChange={(e) => setFormData({ ...formData, image_url: e.target.value })} 
                    placeholder="输入图片URL或点击上传 / Enter URL or upload" 
                    className="flex-1" 
                    data-testid="about-image-input" 
                  />
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept="image/*"
                    onChange={handleImageUpload}
                    className="hidden"
                  />
                  <Button 
                    type="button" 
                    variant="outline" 
                    onClick={() => fileInputRef.current?.click()}
                    disabled={uploading}
                  >
                    <Upload className="w-4 h-4 mr-2" />
                    {uploading ? '上传中...' : '上传 / Upload'}
                  </Button>
                </div>
                
                {formData.image_url && (
                  <div className="mt-4 relative inline-block">
                    <img 
                      src={getFullImageUrl(formData.image_url)} 
                      alt="预览" 
                      className="max-w-xs max-h-48 object-cover rounded border"
                      onError={(e) => {
                        e.target.src = 'data:image/svg+xml,<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 100 100"><rect fill="%23f3f4f6" width="100" height="100"/><text x="50" y="50" dominant-baseline="middle" text-anchor="middle" fill="%239ca3af" font-size="12">加载失败</text></svg>';
                      }}
                    />
                    <button
                      type="button"
                      onClick={handleClearImage}
                      className="absolute -top-2 -right-2 w-6 h-6 bg-red-500 text-white rounded-full flex items-center justify-center hover:bg-red-600"
                    >
                      <X className="w-4 h-4" />
                    </button>
                  </div>
                )}
              </div>
            </CardContent>
            <CardFooter>
              <Button onClick={handleSave} disabled={saving} data-testid="save-about-button">
                {saving ? '保存中...' : '保存中英文内容'}
              </Button>
            </CardFooter>
          </Card>
        </TabsContent>

        {/* ArtCanvas设置 */}
        <TabsContent value="artcanvas">
          <div className="space-y-6">
            {/* Hero区域设置 */}
            <Card>
              <CardHeader>
                <CardTitle>Hero区域</CardTitle>
                <CardDescription>ArtCanvas页面顶部展示区域</CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                {/* Hero背景图 */}
                <div className="space-y-2">
                  <Label>背景图片（名画）</Label>
                  <div className="flex gap-2">
                    <Input 
                      value={artCanvasData.hero_background_image} 
                      onChange={(e) => setArtCanvasData({ ...artCanvasData, hero_background_image: e.target.value })} 
                      placeholder="输入图片URL或上传本地图片" 
                      className="flex-1" 
                    />
                    <input
                      ref={heroBgInputRef}
                      type="file"
                      accept="image/*"
                      onChange={handleHeroBgUpload}
                      className="hidden"
                    />
                    <Button 
                      type="button" 
                      variant="outline" 
                      onClick={() => heroBgInputRef.current?.click()}
                      disabled={uploadingHeroBg}
                    >
                      <Upload className="w-4 h-4 mr-2" />
                      {uploadingHeroBg ? '上传中...' : '上传'}
                    </Button>
                  </div>
                  <p className="text-sm text-amber-600">建议使用高清名画作为背景，当前默认：Lady Godiva（戈黛娃夫人）</p>
                  
                  {artCanvasData.hero_background_image && (
                    <div className="mt-4 relative inline-block">
                      <img 
                        src={getFullImageUrl(artCanvasData.hero_background_image)} 
                        alt="Hero背景预览" 
                        className="max-w-md max-h-48 object-cover rounded border"
                        onError={(e) => {
                          e.target.src = 'data:image/svg+xml,<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 100 100"><rect fill="%23f3f4f6" width="100" height="100"/><text x="50" y="50" dominant-baseline="middle" text-anchor="middle" fill="%239ca3af" font-size="12">加载失败</text></svg>';
                        }}
                      />
                      <button
                        type="button"
                        onClick={handleClearHeroBgImage}
                        className="absolute -top-2 -right-2 w-6 h-6 bg-red-500 text-white rounded-full flex items-center justify-center hover:bg-red-600"
                      >
                        <X className="w-4 h-4" />
                      </button>
                    </div>
                  )}
                </div>

                <div className="space-y-2">
                  <Label>主标题</Label>
                  <Input 
                    value={artCanvasData.hero_title} 
                    onChange={(e) => setArtCanvasData({ ...artCanvasData, hero_title: e.target.value })} 
                    placeholder="ArtCanvas" 
                  />
                </div>
                <div className="space-y-2">
                  <Label>副标题</Label>
                  <Input 
                    value={artCanvasData.hero_subtitle} 
                    onChange={(e) => setArtCanvasData({ ...artCanvasData, hero_subtitle: e.target.value })} 
                    placeholder="发现独特的艺术之美" 
                  />
                </div>
              </CardContent>
            </Card>

            {/* 有机棉包服务设置 */}
            <Card>
              <CardHeader>
                <CardTitle>有机棉包定制服务</CardTitle>
                <CardDescription>设置有机棉包展示图和相关文案</CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="space-y-2">
                  <Label>展示图片</Label>
                  <div className="flex gap-2">
                    <Input 
                      value={artCanvasData.tote_bag_image} 
                      onChange={(e) => setArtCanvasData({ ...artCanvasData, tote_bag_image: e.target.value })} 
                      placeholder="输入图片URL或上传本地图片" 
                      className="flex-1" 
                    />
                    <input
                      ref={toteBagInputRef}
                      type="file"
                      accept="image/*"
                      onChange={handleToteBagUpload}
                      className="hidden"
                    />
                    <Button 
                      type="button" 
                      variant="outline" 
                      onClick={() => toteBagInputRef.current?.click()}
                      disabled={uploadingToteBag}
                    >
                      <Upload className="w-4 h-4 mr-2" />
                      {uploadingToteBag ? '上传中...' : '上传'}
                    </Button>
                  </div>
                  
                  {artCanvasData.tote_bag_image && (
                    <div className="mt-4 relative inline-block">
                      <img 
                        src={getFullImageUrl(artCanvasData.tote_bag_image)} 
                        alt="有机棉包预览" 
                        className="max-w-sm max-h-64 object-cover rounded border"
                        onError={(e) => {
                          e.target.src = 'data:image/svg+xml,<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 100 100"><rect fill="%23f3f4f6" width="100" height="100"/><text x="50" y="50" dominant-baseline="middle" text-anchor="middle" fill="%239ca3af" font-size="12">加载失败</text></svg>';
                        }}
                      />
                      <button
                        type="button"
                        onClick={handleClearToteBagImage}
                        className="absolute -top-2 -right-2 w-6 h-6 bg-red-500 text-white rounded-full flex items-center justify-center hover:bg-red-600"
                      >
                        <X className="w-4 h-4" />
                      </button>
                    </div>
                  )}
                </div>

                <div className="space-y-2">
                  <Label>标题</Label>
                  <Input 
                    value={artCanvasData.tote_bag_title} 
                    onChange={(e) => setArtCanvasData({ ...artCanvasData, tote_bag_title: e.target.value })} 
                    placeholder="把你的作品变成艺术品" 
                  />
                </div>

                <div className="space-y-2">
                  <Label>描述</Label>
                  <Textarea 
                    value={artCanvasData.tote_bag_description} 
                    onChange={(e) => setArtCanvasData({ ...artCanvasData, tote_bag_description: e.target.value })} 
                    placeholder="我们将你的画作印制在100%有机棉包上..." 
                    rows={3}
                  />
                </div>

                <div className="space-y-2">
                  <Label>版税比例 (%)</Label>
                  <Input 
                    type="number"
                    min="0"
                    max="100"
                    value={artCanvasData.royalty_percentage} 
                    onChange={(e) => setArtCanvasData({ ...artCanvasData, royalty_percentage: parseInt(e.target.value) || 10 })} 
                    className="w-32"
                  />
                  <p className="text-sm text-amber-600">艺术家每次售出可获得的收入比例</p>
                </div>

                <div className="space-y-2">
                  <Label>特性列表</Label>
                  <div className="space-y-2">
                    {(artCanvasData.tote_bag_features || []).map((feature, index) => (
                      <div key={index} className="flex items-center gap-2">
                        <Input 
                          value={feature}
                          onChange={(e) => {
                            const newFeatures = [...(artCanvasData.tote_bag_features || [])];
                            newFeatures[index] = e.target.value;
                            setArtCanvasData({ ...artCanvasData, tote_bag_features: newFeatures });
                          }}
                          className="flex-1"
                        />
                        <Button 
                          type="button" 
                          variant="ghost" 
                          size="icon"
                          onClick={() => {
                            const newFeatures = (artCanvasData.tote_bag_features || []).filter((_, i) => i !== index);
                            setArtCanvasData({ ...artCanvasData, tote_bag_features: newFeatures });
                          }}
                          className="text-red-500 hover:text-red-600"
                        >
                          <X className="w-4 h-4" />
                        </Button>
                      </div>
                    ))}
                    <div className="flex items-center gap-2">
                      <Input 
                        value={newFeature}
                        onChange={(e) => setNewFeature(e.target.value)}
                        placeholder="添加新特性..."
                        className="flex-1"
                        onKeyPress={(e) => {
                          if (e.key === 'Enter' && newFeature.trim()) {
                            e.preventDefault();
                            setArtCanvasData({ 
                              ...artCanvasData, 
                              tote_bag_features: [...(artCanvasData.tote_bag_features || []), newFeature.trim()]
                            });
                            setNewFeature('');
                          }
                        }}
                      />
                      <Button 
                        type="button" 
                        variant="outline"
                        onClick={() => {
                          if (newFeature.trim()) {
                            setArtCanvasData({ 
                              ...artCanvasData, 
                              tote_bag_features: [...(artCanvasData.tote_bag_features || []), newFeature.trim()]
                            });
                            setNewFeature('');
                          }
                        }}
                      >
                        添加
                      </Button>
                    </div>
                  </div>
                  <p className="text-sm text-amber-600">显示在定制服务区域的特性亮点</p>
                </div>

                <div className="space-y-2">
                  <Label>了解详情链接</Label>
                  <Input 
                    value={artCanvasData.tote_bag_detail_link || ''} 
                    onChange={(e) => setArtCanvasData({ ...artCanvasData, tote_bag_detail_link: e.target.value })} 
                    placeholder="/about 或 https://..." 
                  />
                  <p className="text-sm text-amber-600">点击"了解详情"按钮跳转的页面</p>
                </div>
              </CardContent>
              <CardFooter>
                <Button onClick={handleSaveArtCanvas} disabled={savingArtCanvas}>
                  {savingArtCanvas ? '保存中...' : '保存ArtCanvas设置'}
                </Button>
              </CardFooter>
            </Card>
          </div>
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default AdminSettings;
